import React from "react";
import { motion } from "framer-motion";
import { useDispatch, useSelector } from "react-redux";
import { updateSettings } from "../actions/user.action";
import { useTranslation } from "react-i18next";

import SettingConstants from "../constants/settings";
import useDarkSide from "../hooks/useDarkSide";
import SwitchButton from "../components/buttons/SwitchButton";
import TailwindSelect from "../components/buttons/TailwindSelect";

const SettingPage = () => {
  const { t } = useTranslation();

  const { settings } = useSelector(({ user }) => user);
  const dispatch = useDispatch();

  const handleUpdate = (key, value) => {
    dispatch(updateSettings({ ...settings, [key]: value }));
  };

  const [theme, setTheme] = useDarkSide();

  return (
    <motion.div
      className="z-1 relative flex w-full max-w-lg select-none flex-col"
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0 }}
      transition={{ duration: 0.2, type: "spring", mass: 1 }}
    >
      <div className="relative flex w-full max-w-lg flex-grow flex-col">
        <div className="mt-8 px-2 md:px-0">
          <h3 className="text-center text-xl font-bold">{t("Settings")}</h3>
          <div className="my-4 flex flex-col gap-2">
            <div className="flex p-1">
              <TailwindSelect
                id="setting-distanceUnit"
                value={SettingConstants.distanceUnits.find(
                  (item) => item.value === settings.distanceUnit,
                )}
                onChange={(item) => {
                  handleUpdate("distanceUnit", item.value);
                }}
                options={SettingConstants.distanceUnits}
              />
              <label
                className="ml-4 min-w-48 flex-1 leading-8"
                htmlFor="setting-distanceUnit"
              >
                {t("Unit of distance")}
              </label>
            </div>
            <div className="flex px-4 py-2">
              <SwitchButton
                id="setting-theme"
                onChange={(e) => {
                  setTheme(e.target.checked ? "light" : "dark");
                }}
                checked={theme === "light"}
              />
              <label className="ml-4 flex-1" htmlFor="setting-theme">
                {t("Dark") + "/" + t("Light") + " " + t("Theme")}
              </label>
            </div>
            <div className="flex p-1">
              <TailwindSelect
                id="setting-language"
                value={SettingConstants.languages.find(
                  (item) => item.value === settings.language,
                )}
                onChange={(item) => {
                  handleUpdate("language", item.value);
                }}
                options={SettingConstants.languages}
              />
              <label
                className="ml-4 min-w-48 flex-1 leading-8"
                htmlFor="setting-language"
              >
                {t("Language")}
              </label>
            </div>
          </div>
          <div className="mt-4 flex flex-col gap-2">
            <header className="my-4">
              <h3 className="text-center text-lg font-bold">
                {t("Difficulty")}
              </h3>
            </header>
            <div className="flex px-4 py-1">
              <SwitchButton
                id="setting-noImage"
                onChange={(e) => {
                  handleUpdate("noImage", e.target.checked);
                }}
                checked={settings.noImage}
              />
              <label className="ml-4 flex-1" htmlFor="setting-noImage">
                {t("Hide country image for more of a challenge.")}
              </label>
            </div>
            <div className="flex px-4 py-1">
              <SwitchButton
                id="setting-rotationMode"
                onChange={(e) => {
                  handleUpdate("rotationMode", e.target.checked);
                }}
                checked={settings.rotationMode}
              />
              <label className="ml-4 flex-1" htmlFor="setting-rotationMode">
                {t("Randomly rotate country image.")}
              </label>
            </div>
            <div className="flex px-4 py-1">
              <SwitchButton
                id="setting-showScale"
                onChange={(e) => {
                  handleUpdate("showScale", e.target.checked);
                }}
                checked={settings.showScale}
              />
              <label className="ml-4 flex-1" htmlFor="setting-showScale">
                {t("Replace proximity percent by size percent.")}
              </label>
            </div>
            <div className="flex px-4 py-1">
              <SwitchButton
                id="setting-sortNeighboursAlphabetically"
                onChange={(e) => {
                  handleUpdate(
                    "sortNeighboursAlphabetically",
                    e.target.checked,
                  );
                }}
                checked={settings.sortNeighboursAlphabetically}
              />

              <label
                className="ml-4 flex-1"
                htmlFor="setting-sortNeighboursAlphabetically"
              >
                {t("Sort neighbouring countries alphabetically.")}
              </label>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SettingPage;
